import { useMutation } from '@tanstack/react-query';
import { Card, CardHeader } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { api, ApiError } from '@/lib/api';
import { useActiveCompanyId } from '@/lib/active-company';
import { useSubscription } from '@/lib/companies';

export function SubscriptionTab() {
  const id = useActiveCompanyId();
  const sub = useSubscription(id);

  const checkout = useMutation({
    mutationFn: () =>
      api.post<{ url: string }>('/api/billing/checkout-session', {
        successUrl: `${window.location.origin}/settings/subscription?billing=success`,
        cancelUrl: `${window.location.origin}/settings/subscription`,
      }),
    onSuccess: (data) => {
      window.location.assign(data.url);
    },
  });

  const portal = useMutation({
    mutationFn: () =>
      api.post<{ url: string }>('/api/billing/portal-session', {
        returnUrl: `${window.location.origin}/settings/subscription`,
      }),
    onSuccess: (data) => window.location.assign(data.url),
    onError: (err) => {
      // No Stripe customer yet → fall back to checkout.
      if (err instanceof ApiError && err.status === 400) {
        checkout.mutate();
      }
    },
  });

  if (!id) return <p>Select a company first.</p>;

  return (
    <div className="flex flex-col gap-gap-element-lg">
      <Card>
        <CardHeader
          title="Subscription"
          description="Manage your plan, billing, and payment method."
          action={sub.data ? <StatusBadge status={sub.data.status} /> : null}
        />

        {sub.data ? (
          <ActiveBlock
            sub={sub.data}
            onManage={() => portal.mutate()}
            onSubscribe={() => checkout.mutate()}
            busy={checkout.isPending || portal.isPending}
          />
        ) : (
          <NoSubscriptionBlock
            onSubscribe={() => checkout.mutate()}
            busy={checkout.isPending}
          />
        )}
      </Card>
    </div>
  );
}

function StatusBadge({ status }: { status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' }) {
  const tone =
    status === 'ACTIVE'
      ? 'success'
      : status === 'TRIAL'
        ? 'accent'
        : status === 'PAST_DUE'
          ? 'warning'
          : 'danger';
  return <Badge tone={tone}>{status.toLowerCase()}</Badge>;
}

function ActiveBlock({
  sub,
  onManage,
  onSubscribe,
  busy,
}: {
  sub: {
    tier: string;
    status: string;
    currentPeriodEnd: string | null;
    isReadOnly: boolean;
  };
  onManage: () => void;
  onSubscribe: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex flex-col gap-gap-element">
      <dl className="grid gap-gap-element md:grid-cols-3">
        <Row label="Plan" value={sub.tier} />
        <Row
          label="Renews on"
          value={
            sub.currentPeriodEnd
              ? new Date(sub.currentPeriodEnd).toLocaleDateString()
              : '—'
          }
        />
        <Row label="Status" value={sub.status} />
      </dl>

      {sub.isReadOnly ? (
        <div className="rounded-md border border-warning/40 bg-warning/5 px-4 py-3 text-body-sm text-warning">
          Your subscription is past its grace period. The app is in read-only
          mode until you reactivate.
        </div>
      ) : null}

      <div className="flex gap-3">
        <Button onClick={onManage} disabled={busy}>
          Manage billing
        </Button>
        {sub.isReadOnly ? (
          <Button variant="secondary" onClick={onSubscribe} disabled={busy}>
            Reactivate subscription
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function NoSubscriptionBlock({
  onSubscribe,
  busy,
}: {
  onSubscribe: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex flex-col gap-gap-element">
      <p className="text-body text-text-secondary">
        You don't have an active subscription. Subscribe to unlock the full
        assessment toolset for your company. You can pay by card or US bank
        transfer (ACH).
      </p>
      <div>
        <Button onClick={onSubscribe} disabled={busy}>
          {busy ? 'Loading…' : 'Subscribe'}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-label uppercase tracking-[0.08em] text-text-secondary">
        {label}
      </dt>
      <dd className="mt-1 font-medium">{value}</dd>
    </div>
  );
}
